import {
    AccordionSummary,
    Typography,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    SxProps,
    Stack,
    IconButton,
    Theme
} from '@mui/material';
import {
    Remove as RemoveIcon,
    Add as AddIcon,
    EditOutlined as EditOutlinedIcon
} from '@mui/icons-material';
import { useState } from 'react';
import { ReviewConfigDto, ReviewExtendReasonDto, ReviewTimeDto } from '../../lib/dtos/ReviewDtos';
import {
    StyledAccordionContainer,
    StyledGlobalSwitch,
    StyledInnerAccordion
} from '../ConfigurePage.styles';
import ReviewTimeModal from './ReviewTimeModal';
import AddButton from './AddButton';
import ReviewExtendReasonModal from './ReviewExtendReasonModal';
import { post } from '../../lib/api';
import { AxiosError } from 'axios';
import { toast, ToastContentProps } from 'react-toastify';
import { ErrorResponse } from '../../lib/dtos/ErrorResponse';
import mapObject from '../../lib/helper/mapper';

interface ReviewConfigProps {
    dto: ReviewConfigDto;
    isExpanded: boolean;
}

const ClinicalReviewSection = ({ dto }: ReviewConfigProps) => {
    const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

    const [reviewTimes, setReviewTimes] = useState(dto.reviewTimes);
    const [reviewExtendReasons, setReviewExtendReasons] = useState(dto.reviewExtendReasons);

    const [currentReviewTime, setCurrentReviewTime] = useState<ReviewTimeDto | null>(null);
    const [isTimeOpen, setIsTimeOpen] = useState(false);

    const [currentReviewExtendReason, setCurrentReviewExtendReason] =
        useState<ReviewExtendReasonDto | null>(null);
    const [isReasonOpen, setIsReasonOpen] = useState(false);

    const sectionTitle = 'Clinical Reviews';
    const reviewTimeTitle = 'How long did this review take?';
    const reviewExtendReasonTitle = 'Reasoning for extended review';

    const handleChange = (isExpanded: boolean, panel: string) => {
        isExpanded
            ? setExpandedPanels((expandedPanels: string[]) => [
                  ...expandedPanels,
                  isExpanded ? panel : ''
              ])
            : setExpandedPanels((expandedPanels: string[]) =>
                  expandedPanels.filter((n: string) => !n.startsWith(panel))
              );
    };

    const innerBackground: SxProps<Theme> = (t) => ({
        backgroundColor: t.palette.listItemBackground.shade,
        marginBottom: '0.188rem'
    });

    const upsertReviewExtendReason = async (formData: ReviewExtendReasonDto) => {
        const { data: upsertedData, status } = await post<ReviewExtendReasonDto>(
            'appconfigs/upsertreviewextendreason',
            formData
        );

        if (status === 200) {
            setReviewExtendReasons((prev) => {
                const data = prev.find((s) => s.id === upsertedData.id);

                if (data) {
                    mapObject(upsertedData, data);
                    return [...prev];
                } else {
                    const newState = prev.filter((s) => s.id !== upsertedData.id);
                    newState.push(upsertedData);
                    return newState;
                }
            });
        }
    };

    const upsertReviewTime = async (formData: ReviewTimeDto) => {
        const { data: upsertedData, status } = await post<ReviewTimeDto>(
            'appconfigs/upsertreviewtime',
            formData
        );

        if (status === 200) {
            setReviewTimes((prev) => {
                const arr = prev.filter((s) => s.id !== upsertedData.id);

                if (upsertedData.isDefault) {
                    for (const data of arr) {
                        data.isDefault = false;
                    }
                }

                arr.push(upsertedData);

                const newState = arr.sort((a, b) => a.minutes - b.minutes);

                return newState;
            });
        }
    };

    const reviewTimeEditButtonClickHandler = (data: ReviewTimeDto) => {
        setCurrentReviewTime(data);
        setIsTimeOpen(true);
    };
    const reviewTimeAddButtonClickHandler = () => {
        setCurrentReviewTime(null);
        setIsTimeOpen(true);
    };

    const reviewTimeModalSubmitHandler = async (formData: ReviewTimeDto) => {
        await upsertReviewTime(formData);
    };
    const reviewTimeModalDialogCloseHandler = () => {
        setIsTimeOpen(false);
    };

    const reviewTimeIsActiveChangeHandler = async (data: ReviewTimeDto, checked: boolean) => {
        await toast.promise(upsertReviewTime({ ...data, isActive: checked }), {
            success: 'Success',
            pending: 'Updating value...',
            error: {
                render: ({ data }: ToastContentProps<AxiosError<ErrorResponse>>) => {
                    return data?.response?.data.message;
                }
            }
        });
    };
    // reason
    const reviewReasonEditButtonClickHandler = (data: ReviewExtendReasonDto) => {
        setCurrentReviewExtendReason(data);
        setIsReasonOpen(true);
    };
    const reviewReasonAddButtonClickHandler = () => {
        setCurrentReviewExtendReason(null);
        setIsReasonOpen(true);
    };

    const reviewReasonModalSubmitHandler = async (formData: ReviewExtendReasonDto) => {
        await upsertReviewExtendReason(formData);
    };
    const reviewReasonModalDialogCloseHandler = () => {
        setIsReasonOpen(false);
    };

    const reviewReasonIsActiveChangeHandler = async (
        data: ReviewExtendReasonDto,
        checked: boolean
    ) => {
        await toast.promise(upsertReviewExtendReason({ ...data, isActive: checked }), {
            success: 'Success',
            pending: 'Updating value...',
            error: {
                render: ({ data }: ToastContentProps<AxiosError<ErrorResponse>>) => {
                    return data?.response?.data.message;
                }
            }
        });
    };

    return (
        <StyledAccordionContainer
            expanded={
                expandedPanels instanceof Array ? expandedPanels.includes(sectionTitle) : false
            }
            disableGutters={true}
            onChange={(_: unknown, isExpanded: boolean) => handleChange(isExpanded, sectionTitle)}>
            <AccordionSummary
                expandIcon={expandedPanels.includes(sectionTitle) ? <RemoveIcon /> : <AddIcon />}>
                <Typography variant="h3">{sectionTitle}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <List>
                    {/* Technical Support */}
                    <StyledInnerAccordion
                        sx={{ marginBottom: '0' }}
                        expanded={
                            expandedPanels instanceof Array
                                ? expandedPanels.includes(reviewTimeTitle)
                                : false
                        }
                        disableGutters={true}
                        elevation={0}
                        onChange={(_: unknown, isExpanded: boolean) =>
                            handleChange(isExpanded, reviewTimeTitle)
                        }>
                        <AccordionSummary
                            expandIcon={
                                expandedPanels.includes(reviewTimeTitle) ? (
                                    <RemoveIcon />
                                ) : (
                                    <AddIcon />
                                )
                            }>
                            <Typography variant="h4">{reviewTimeTitle}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {reviewTimes.map((data) => (
                                    <Stack key={data.id}>
                                        <ListItem sx={innerBackground}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h4">
                                                        {data.isDefault
                                                            ? data.minutes + ' minutes (Standard)'
                                                            : data.minutes + ' minutes'}
                                                    </Typography>
                                                }
                                            />
                                            <Stack direction={'row'} alignItems={'center'} gap={1}>
                                                <IconButton
                                                    onClick={() =>
                                                        reviewTimeEditButtonClickHandler.call(
                                                            null,
                                                            data
                                                        )
                                                    }
                                                    aria-label="edit">
                                                    <EditOutlinedIcon
                                                        sx={{
                                                            color: (t) => t.palette.primary.main
                                                        }}
                                                    />
                                                </IconButton>
                                                <StyledGlobalSwitch
                                                    edge="end"
                                                    checked={data.isActive}
                                                    inputProps={{
                                                        'aria-labelledby': `switch-list-label-contact`
                                                    }}
                                                    onChange={(e, checked) =>
                                                        reviewTimeIsActiveChangeHandler.call(
                                                            null,
                                                            data,
                                                            checked
                                                        )
                                                    }
                                                />
                                            </Stack>
                                        </ListItem>
                                    </Stack>
                                ))}
                                <AddButton
                                    text="Add Review Time"
                                    onClick={reviewTimeAddButtonClickHandler}
                                />
                                <ReviewTimeModal
                                    open={isTimeOpen}
                                    onDialogClose={reviewTimeModalDialogCloseHandler}
                                    onSubmit={reviewTimeModalSubmitHandler}
                                    reviewTime={currentReviewTime}
                                />
                            </List>
                        </AccordionDetails>
                    </StyledInnerAccordion>

                    <StyledInnerAccordion
                        sx={{ marginBottom: '0' }}
                        expanded={
                            expandedPanels instanceof Array
                                ? expandedPanels.includes(reviewExtendReasonTitle)
                                : false
                        }
                        disableGutters={true}
                        elevation={0}
                        onChange={(_: unknown, isExpanded: boolean) =>
                            handleChange(isExpanded, reviewExtendReasonTitle)
                        }>
                        <AccordionSummary
                            expandIcon={
                                expandedPanels.includes(reviewExtendReasonTitle) ? (
                                    <RemoveIcon />
                                ) : (
                                    <AddIcon />
                                )
                            }>
                            <Typography variant="h4">{reviewExtendReasonTitle}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <List>
                                {reviewExtendReasons.map((data) => (
                                    <Stack key={data.id}>
                                        <ListItem sx={innerBackground}>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="h4">
                                                        {data.reason}
                                                    </Typography>
                                                }
                                            />
                                            <Stack direction={'row'} alignItems={'center'} gap={1}>
                                                <IconButton
                                                    onClick={() =>
                                                        reviewReasonEditButtonClickHandler.call(
                                                            null,
                                                            data
                                                        )
                                                    }
                                                    aria-label="edit">
                                                    <EditOutlinedIcon
                                                        sx={{
                                                            color: (t) => t.palette.primary.main
                                                        }}
                                                    />
                                                </IconButton>
                                                <StyledGlobalSwitch
                                                    edge="end"
                                                    checked={data.isActive}
                                                    inputProps={{
                                                        'aria-labelledby': `switch-list-label-contact`
                                                    }}
                                                    onChange={(e, checked) =>
                                                        reviewReasonIsActiveChangeHandler.call(
                                                            null,
                                                            data,
                                                            checked
                                                        )
                                                    }
                                                />
                                            </Stack>
                                        </ListItem>
                                    </Stack>
                                ))}
                                <AddButton
                                    text="Add Review Extend Reason"
                                    onClick={reviewReasonAddButtonClickHandler}
                                />
                                <ReviewExtendReasonModal
                                    open={isReasonOpen}
                                    onDialogClose={reviewReasonModalDialogCloseHandler}
                                    onSubmit={reviewReasonModalSubmitHandler}
                                    reviewExtendReason={currentReviewExtendReason}
                                />
                            </List>
                        </AccordionDetails>
                    </StyledInnerAccordion>
                </List>
            </AccordionDetails>
        </StyledAccordionContainer>
    );
};

export default ClinicalReviewSection;
